/**
 * Vigilancia de version nueva.
 *
 * El problema que resuelve: un usuario deja el CRM abierto en una pestaña,
 * alguien despliega un build nuevo y su pantalla sigue con la versión anterior
 * —o peor, con una página en blanco porque el HTML cacheado apunta a ficheros
 * que ya no existen—. Recargar a mano lo arreglaba, pero nadie recarga si no
 * ve que algo va mal, y quien lo hacía se encontraba con que tampoco
 * funcionaba.
 *
 * La aplicación pregunta cada pocos minutos (y al volver a la pestaña) qué
 * build está sirviendo el servidor. Cuando es distinto del que corre, se
 * recarga una sola vez: ya no hace falta incógnito ni borrar datos.
 *
 * La recarga es a ciegas de una vez, sin aviso intermedio, a propósito: un
 * diálogo de «hay una versión nueva» exige un clic más, y este es un cambio de
 * versión, no una decisión. Lo que se perdería sin recargar —medio formulario—
 * queda guardado en el autoguardado y en la cola offline, que es lo bastante.
 */

/** Identificador del build con el que corre esta pestaña. */
declare const __BUILD_ID__: string | undefined

const BUILD_ID: string = typeof __BUILD_ID__ === 'undefined' ? 'dev' : __BUILD_ID__

/** Cada cuánto se pregunta al servidor, en milisegundos. */
const INTERVALO_MS = 3 * 60 * 1000

/**
 * Marca de que esta pestaña ya se recargó por un cambio de versión.
 *
 * Vive en `sessionStorage` (por pestaña, no para siempre) y es la red de
 * seguridad contra un bucle: si el servidor sirve un `version.json` que nunca
 * coincide con el del bundle —un despliegue a medias, por ejemplo—, sin esta
 * marca la página se recargaría sin parar.
 */
const MARCA_RECARGA = 'crm-recarga-por-version'

let vigíaActivo = false

async function buildDelServidor(): Promise<string | null> {
  try {
    const respuesta = await fetch('/version.json', {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })

    if (!respuesta.ok) return null

    const datos = (await respuesta.json()) as { build?: unknown }

    return typeof datos.build === 'string' ? datos.build : null
  } catch {
    // Sin red o sin fichero: no se puede saber. No se hace nada, que es mejor
    // que recargar por una sospecha.
    return null
  }
}

function recargarSiCambio(): Promise<void> {
  return buildDelServidor().then((build) => {
    if (build === null || build === BUILD_ID) return

    if (sessionStorage.getItem(MARCA_RECARGA) === build) {
      // Esta pestaña ya se recargó por este mismo build y sigue viéndolo: si se
      // recarga otra vez, es que el servidor no está sirviendo lo que dice.
      return
    }

    sessionStorage.setItem(MARCA_RECARGA, build)
    window.location.reload()
  })
}

/**
 * Arranca la vigilancia. Se puede llamar sin miedo: solo actúa una vez.
 *
 * `visibilitychange` además del intervalo porque es el momento en el que el
 * usuario vuelve a la pestaña después de un rato —justo cuando un despliegue
 * ha tenido tiempo de ocurrir—, y esperar al siguiente intervalo le haría
 * seguir viendo la versión vieja un rato más.
 */
export function vigiarVersionNueva(): void {
  if (vigíaActivo) return

  vigíaActivo = true

  window.setInterval(() => {
    void recargarSiCambio()
  }, INTERVALO_MS)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void recargarSiCambio()
    }
  })
}

/** El build con el que corre esta pestaña. Expuesto para las pruebas. */
export function buildActual(): string {
  return BUILD_ID
}
